import React, { useEffect, useState } from 'react';
import { Alert, Box, Button, Typography } from '@mui/material';
import LoadingIndicator from '../../components/common/LoadingIndicator';
import { useTranslation } from 'react-i18next';
import useApi, { Status } from '../../common/hooks/useApi';
import {
  GetProfile,
  UpdateProfile,
  GetNewsletterPrefs,
  UpdateNewsletterPrefs,
} from '../../services/profile/ProfileService';
import { GetUserInterests, UpdateUserInterests } from '../../services/interests/InterestService';
import { createUpdateProfilePayload } from '../../services/profile/UpdateProfilePayload';
import { createUpdateUserInterestsPayload } from '../../services/interests/UpdateUserInterestsPayload';
import CommunicationPreferencesForm, {
  CommunicationPreferencesFormValues,
} from '../../components/common/Forms/CommunicationPreferencesForm';
import { SubscribeToNewsletter } from '../../services/newsletter/NewsLetterService';
import { createUpdateNewsletterPrefsPayload } from '../../services/profile/UpdateNewsletterPrefsPayload';
import { useAuth0 } from '@auth0/auth0-react';
import { useLoading } from './PreferencesBase';
import { SetConsentValues } from './ConsentValue';
import { noChoice } from 'common/FLSConst';
import { Helmet } from 'react-helmet';
import { SyncAccount } from 'components/common/syncAccount';

const CommunicationPage = (): JSX.Element => {
  const { t } = useTranslation('component');
  const { user } = useAuth0();
  const { isLoading, setIsLoading } = useLoading();

  const [fetchProfile, profile, fetchingProfileStatus] = useApi(GetProfile);
  const [fetchUserInterests, userInterests, fetchingUserInterestsStatus] = useApi(GetUserInterests);
  const [fetchNewsletterPrefs, newsletterPrefs, fetchingNewsletterPrefsStatus] = useApi(GetNewsletterPrefs);

  const [saveMessage, setSaveMessage] = useState({ message: '', type: '' });
  const [showInterestPtsBox, setShowInterestPtsBox] = useState<boolean>(true);
  const [showNewsletterPtsBox, setShowNewsletterPtsBox] = useState<boolean>(true);
  const [communicationPreferences, setCommunicationPreferences] = useState<CommunicationPreferencesFormValues | null>(
    null
  );
  const [showSmsPtsBox, setShowSmsPtsBox] = useState<boolean>(true);

  useEffect(() => {
    // Fetch user data when component mounts
    fetchProfile(user?.session_id);
    fetchNewsletterPrefs(user?.session_id);
    fetchUserInterests(user?.session_id);
  }, [fetchProfile, fetchNewsletterPrefs, fetchUserInterests, user?.session_id]);

  useEffect(() => {
    // Initialize form values with the results of the user data api calls
    if (profile && userInterests) {
      setShowNewsletterPtsBox(profile?.Card && profile?.Card?.BonusStillToGet?.MailOptinBonus > 0);
      setShowInterestPtsBox(profile?.Card && profile?.Card?.BonusStillToGet?.InterestBonus > 0);
      setCommunicationPreferences({
        hasMobilePhone: profile.Phones.CellPhone == null || profile.Phones.CellPhone.trim() === '' ? false : true,
        smsConsent: profile.Consents.Sms,
        profilingConsent: profile.Consents.Profiling,
        language: profile.Language,
        interestsChecked: userInterests?.Interests.map((userInterest: { Id: number }) => userInterest.Id.toString()),
        newsletterConsent: profile.Consents.Newsletter,
        newsletterTypes:
          Object.keys(newsletterPrefs).length > 0
            ? newsletterPrefs.NewsletterType.map((id: number) => id.toString())
            : [],
      });
      setShowSmsPtsBox(profile?.Card && profile?.Card?.BonusStillToGet?.SmsOptionBonus > 0);
    }
  }, [profile, newsletterPrefs, userInterests]);

  useEffect(() => {
    setIsLoading(
      fetchingProfileStatus === Status.Pending ||
        fetchingNewsletterPrefsStatus === Status.Pending ||
        fetchingUserInterestsStatus === Status.Pending
    );
  }, [fetchingProfileStatus, fetchingNewsletterPrefsStatus, fetchingUserInterestsStatus, setIsLoading]);

  const hasFetchDataFailed =
    fetchingProfileStatus === Status.Rejected || fetchingUserInterestsStatus === Status.Rejected;

  const handleSave = async function (values: CommunicationPreferencesFormValues) {
    if (!isLoading) {
      setIsLoading(true);
      setSaveMessage({ message: '', type: '' });
      try {
        const updatePromises = [];

        if (Object.keys(newsletterPrefs).length === 0 && values.newsletterConsent) {
          await SubscribeToNewsletter(profile.Email, values.language);
        }

        if (newsletterPrefs || values.newsletterConsent) {
          const updateNewsletterPrefsPayload = createUpdateNewsletterPrefsPayload(userInterests);
          updateNewsletterPrefsPayload.NewsletterTypes = values.newsletterTypes.map((id) => parseInt(id));
          updateNewsletterPrefsPayload.ExplicitConsent = SetConsentValues(
            values.newsletterConsent,
            communicationPreferences?.newsletterConsent || false
          );
          updatePromises.push(UpdateNewsletterPrefs(user!.session_id, updateNewsletterPrefsPayload));
        }

        const updateProfilePayload = createUpdateProfilePayload(profile);
        updateProfilePayload.Language = values.language;

        if (values.hasMobilePhone) {
          updateProfilePayload.SmsConsent = SetConsentValues(values.smsConsent, profile.Consents.Sms);
        } else {
          updateProfilePayload.SmsConsent = null;
        }

        updateProfilePayload.ProfilingConsent = SetConsentValues(values.profilingConsent, profile.Consents.Profiling);
        updatePromises.push(UpdateProfile(user!.session_id, updateProfilePayload));

        const updateUserInterestsPayload = createUpdateUserInterestsPayload(userInterests);
        updateUserInterestsPayload.InterestIds = values.interestsChecked.map((id) => parseInt(id));
        if (updateUserInterestsPayload.InterestIds.length == 0) {
          updateUserInterestsPayload.InterestIds = noChoice;
        }
        updatePromises.push(UpdateUserInterests(user!.session_id, updateUserInterestsPayload));

        await Promise.all(updatePromises);
        //Set les nouvelles valeurs comme anciennes pour pouvoir les remodifier immediatement
        profile.Consents.Sms = values.smsConsent;
        profile.Consents.Profiling = values.profilingConsent;
        profile.Consents.Newsletter = values.newsletterConsent;

        setSaveMessage({ message: t('Preferences.SaveSuccess') as string, type: 'success' });
        SyncAccount();
      } catch (e) {
        setSaveMessage({ message: t('Errors.GenericError') as string, type: 'error' });
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <>
      <Helmet>
        <meta name="robots" content="noindex, nofollow" />
        <title>{t('MetaTag.CommunicationPrefTitle')}</title>
        <meta name="description" content={t('MetaTag.CommunicationPrefDescription').toString()} />
      </Helmet>
      {hasFetchDataFailed && (
        <Alert severity="error" sx={{ mb: '16px' }}>
          {t('Preferences.FetchDataFailed')}
        </Alert>
      )}
      {saveMessage.type === 'success' && (
        <Alert severity="success" sx={{ mb: '16px' }}>
          {saveMessage.message}
        </Alert>
      )}

      <Typography mb={'2em'}>{t('Preferences.CommunicationDescription')}</Typography>
      {!communicationPreferences && isLoading && <LoadingIndicator />}
      {communicationPreferences && (
        <CommunicationPreferencesForm
          disableNewsletterContent={Object.keys(newsletterPrefs).length === 0}
          communicationPreferences={communicationPreferences}
          saveHandler={handleSave}
          saveErrorMessage={saveMessage.type === 'error' ? saveMessage.message : null}
          footer={
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'right' }}>
              {isLoading && <LoadingIndicator sx={{ margin: '0 16px 0 0' }} />}
              <Button type="submit" variant="contained" disabled={isLoading}>
                {t('Buttons.Save')}
              </Button>
            </Box>
          }
          showNewsletterPtsBox={showNewsletterPtsBox}
          showInterestPtsBox={showInterestPtsBox}
          showSmsPtsBox={showSmsPtsBox}
        />
      )}
    </>
  );
};

export default CommunicationPage;
